import { cn } from '@heidi/utils';
import { times } from 'lodash-es';
import { type MutableRefObject } from 'react';
import { AudioBar } from '../../audio-bar';
import { BAR_HEIGHT_RANGE } from './barTransform';

interface Props {
  barsRef?: MutableRefObject<HTMLDivElement[]>;
  isSilent?: boolean;
  audioReady?: boolean;
  isError?: boolean;
  className?: string;
  /** Compact size for use in tight spaces like sidebar indicators */
  size?: 'default' | 'compact';
  /**
   * Number of bars to render. With 3, drivers map their 5-way level data
   * onto the centre three bands so the mid-tall-mid arc and per-band
   * responsiveness are preserved (V2 session-header design).
   */
  barCount?: 3 | 5;
}

const sizeStyles = {
  default: {
    container: 'h-5 w-11 gap-0.5 px-1',
    bar: 'h-5 w-1',
  },
  compact: {
    container: 'h-3 w-4 gap-[1px] px-0',
    bar: 'h-3 w-[2px]',
  },
};

export const BarsBase = ({
  barsRef,
  isSilent,
  audioReady,
  isError,
  className,
  size = 'default',
  barCount = 5,
}: Props) => {
  const styles = sizeStyles[size];
  const { min, max } = BAR_HEIGHT_RANGE[size];
  const isDestructive = (isSilent && audioReady) || isError;
  const capClassName = cn(
    'transition-transform duration-100 ease-out motion-reduce:transition-none',
    'bg-validation-success',
    isDestructive && '!bg-validation-destructive'
  );

  return (
    <div
      className={cn(
        'flex items-center justify-around overflow-hidden py-px',
        styles.container,
        className
      )}
    >
      {times(barCount).map((i) => (
        <AudioBar
          key={i}
          ref={(el) => {
            if (barsRef) barsRef.current[i] = el as HTMLDivElement;
          }}
          minHeight={min}
          maxHeight={max}
          // Deliberately NO reducedMotionHeight: these meters are the
          // mic-liveness signal (a frozen bar is indistinguishable from a dead
          // mic), so live level updates stay on under prefers-reduced-motion —
          // matching the legacy bars. Only the cap tween is disabled
          // (`motion-reduce:transition-none`). A designed reduced-motion
          // treatment (discrete low-cadence steps) is a follow-up.
          className={cn('flex-1', styles.bar)}
          capClassName={capClassName}
        />
      ))}
    </div>
  );
};
